import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  Inject,
  isDevMode,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';

import { combineLatest, Observable, Subject } from 'rxjs';
import {
  filter,
  map,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { MatDialog } from '@angular/material/dialog';
import { CookieService } from 'ngx-cookie-service';

import { RouterFacade } from '@cigna/shared/angular/core/router-state-data-access';

import { AuthFacade } from '@cigna/chcp/auth/data-access';
import { Entitlement, LOB, LobPref } from '@cigna/chcp/auth/util';
import {
  ENVIRONMENT_CONFIG,
  EnvironmentConfig,
} from '@cigna/chcp/shared/environment-util';
import { LobPreferenceDialogComponent } from '@cigna/chcp/shared/ui';
import { UserFacade } from '@cigna/chcp/shared/user-profile-data-access';
import {
  ChcpPageBg,
  ChcpPageLayout,
  RouteData,
  UserAlert,
} from '@cigna/chcp/shared/util';
import { ShellFacade } from '@cigna/chcp/shell/data-access';
import { FeatureTogglesFacade } from '@cigna/shared/angular/features-feature';
import { ChcpContentfulService } from '@cigna/chcp/shared/contentful-data-access';
import { Alertv2 } from '@cigna/chcp/shell/ui';
import { RouteGuard } from '@cigna/chcp/shared/features-util';

@Component({
  selector: 'cigna-layout-secure',
  templateUrl: './layout-secure.component.html',
  styleUrls: ['./layout-secure.component.scss'],
})
export class LayoutSecureComponent implements OnInit, OnDestroy {
  firstName$ = this.userFacade.firstName$;
  lastName$ = this.userFacade.lastName$;
  userid$ = this.authFacade.userid$;
  passwordExpiry$ = this.authFacade.passwordExpiry$;
  shouldShowPwBanner$ = this.passwordExpiry$.pipe(
    map((age) => (age && age <= 15) || false),
  );
  hasUndefinedLobPref$ = this.userFacade.hasUndefinedLobPref$;

  impersonator$ = this.authFacade.isImpersonator$;

  profileNav$ = this.shellFacade.profileNav$.pipe(
    map((data) => data.filter((link) => link.identifier !== 'chcp.inbox')),
  );
  siteNav$ = this.shellFacade.siteNav$;

  routeData$ = this.routerFacade.data$ as Observable<RouteData>;
  pageLayout$: Observable<ChcpPageLayout> = this.routeData$.pipe(
    map((data) => data.pageLayout ?? 'default'),
  );
  pageBg$: Observable<ChcpPageBg> = this.routeData$.pipe(
    map((data) => data.pageBg ?? 'default'),
  );
  routeUrl$ = this.routerFacade.url$;

  isResourcePage$ = this.routeData$.pipe(
    map((data) => data.routeCode === 'resources'),
  );
  logoLink$ = this.isResourcePage$.pipe(
    map((isResourcePage) =>
      isResourcePage
        ? // temporary fix until the rest of resource library is built
          `${this.envConfig.api.gateways.cdn}assets/evernorth/provider/resourceLibrary/resourceLibrary.html`
        : '/dashboard',
    ),
  );
  messageCenterEnabled$ = this.authFacade.hasMessageCenterCapability$;
  userAlerts$ = this.userFacade.userAlerts$;

  isRouteGuardLoading$ = this.routeGuard.loader$;
  siteDownFlag$ = this.featureToggleFacade.featuresEnabled([
    'ftr-chcp-global-team-web-site_down',
  ]);
  isTaskAlertsEnabled$ = this.featureToggleFacade.featuresEnabled([
    'ftr-chcp-cloud9-team-web-directory-new-experience',
  ]);

  isLimitedContractingExperience$ = combineLatest([
    this.featureToggleFacade.featuresEnabled([
      'ftr-chcp-nova-team-web-my-practice-contracting-flow',
    ]),
    this.authFacade.hasContractingLimitedOnly$,
  ]).pipe(map(([enabled, limitedOnly]) => enabled && limitedOnly));

  onDestroy$ = new Subject<void>();

  secureAlerts: Alertv2[] = [];
  isApiMock: boolean;

  isInBrowserPlatform: boolean;

  isDevMode = false;

  constructor(
    public shellFacade: ShellFacade,
    private userFacade: UserFacade,
    private authFacade: AuthFacade,
    private routerFacade: RouterFacade,
    private cookieService: CookieService,
    private featureToggleFacade: FeatureTogglesFacade,
    private contentfulService: ChcpContentfulService,
    public dialog: MatDialog,
    private routeGuard: RouteGuard,
    @Inject(PLATFORM_ID) platformId: {},
    @Inject(ENVIRONMENT_CONFIG) private envConfig: EnvironmentConfig,
  ) {
    this.isInBrowserPlatform = isPlatformBrowser(platformId);
    this.isApiMock = this.envConfig.apiMock;
  }

  ngOnInit() {
    /* FOR LOCAL DEV ONLY */
    if (isDevMode() && this.isApiMock) {
      this.isDevMode = true;
    }

    this.shellFacade.loadNav();

    // LaunchDarkly can resolve after the layout mounts, so wait for the flag
    // to turn on instead of acting on its first (possibly default) value
    this.featureToggleFacade
      .featuresEnabled(['ftr-chcp-cloud9-team-web-directory-new-experience'])
      .pipe(filter(Boolean), take(1), takeUntil(this.onDestroy$))
      .subscribe(() => this.userFacade.getUserAlerts());

    combineLatest([this.authFacade.lob$, this.isLimitedContractingExperience$])
      .pipe(
        filter(([lob]) => lob !== undefined && lob !== null),
        take(1),
        withLatestFrom(this.authFacade.entitlements$),
      )
      .subscribe(([[lob, isLimitedContractingExperience], entitlements]) => {
        this.contentfulService
          .getAlerts(
            isLimitedContractingExperience
              ? '1zd6wACJnONaOgWEfj7ynL'
              : '7h9NP7EZPLiaC6CQczBKKg',
            lob as LOB,
            entitlements,
          )
          .then((alerts) => (this.secureAlerts = alerts))
          .catch(() => {
            this.secureAlerts = [];
          });
      });

    // remove deeplink cookie after login unless we are in the middle of mfa flow
    this.routeUrl$
      .pipe(
        filter((url) => !url?.startsWith('/mfa')),
        take(1),
        tap(() => {
          this.cookieService.delete('CHCP_DEEPLINK');
        }),
        takeUntil(this.onDestroy$),
      )
      .subscribe();

    this.hasUndefinedLobPref$
      .pipe(
        take(2),
        withLatestFrom(
          this.authFacade.isImpersonator$,
          this.isLimitedContractingExperience$,
        ),
      )
      .subscribe(
        ([hasUndefinedLobPref, isImpersonator, isLimitedExperience]) => {
          if (hasUndefinedLobPref && !isImpersonator && !isLimitedExperience) {
            this.dialog
              .open(LobPreferenceDialogComponent, {
                disableClose: true,
                autoFocus: false,
              })
              .afterClosed()
              .pipe(take(1))
              .subscribe(({ lobPref }: { lobPref: LobPref }) => {
                if (lobPref) {
                  this.userFacade.updateProfile({ lobPref });
                }
              });
          }
        },
      );
  }

  onStartReview(alert: UserAlert) {
    this.userFacade.updateUserAlerts({ [alert.tin]: 'completed' });
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
